import {
  escapeRegExp, filter, orderBy,
} from 'lodash';
import {
  AccountSearchResultData,
  ESResult,
  RoleSearchResultData,
  SearchResultResponse, TagSearchResultData, UserSearchResultData,
  TrackImportResultData,
} from 'lib/types/globalSearch';
import { FilterResult } from 'lib/types/filters';
import { getAvatarOrPlaceholder } from 'lib/utils/imageUtils';
import { TAG_TYPE_IDS } from 'lib/constants';
import { FilterOptionsState } from '@mui/material/useAutocomplete';

/** It's very slow for the DB to search on short strings with many matches */
export const MINIMUM_INPUT_LENGTH = 2;

// TODO: Eventually we want to standardize on 'roles' and handle 'role' as the special case.
function esModelToQuery(model: string, isCollaborator = false) {
  if (model === 'roles') {
    if (isCollaborator) {
      return 'byCollaboratorRole';
    }
    return 'role';
  }

  if (model === 'postingAccounts') {
    return 'postingAccount';
  }

  if (model === 'roleTags') {
    return 'byCollaboratorType';
  }

  if (model === 'users' && isCollaborator) {
    return 'byCollaborator';
  }

  return model;
}

/**
 * Formats the second line of the filter results.
 * - If there is a subtitle, use it.
 * - If it's `postingAccount` use 'posting account'.
 * - Otherwise, use a singular version of the model.
 */
function formatESSubLabel(result: ESResult) {
  if (result.subtitle) {
    return result.subtitle;
  }

  if (result.model === 'postingAccounts') {
    return 'posting account';
  }

  if (result.model === 'roleTags') {
    return 'category';
  }

  return result.model.replace(/s$/, '');
}

export const formatUserResult = (result: UserSearchResultData): FilterResult => ({
  id: result.id,
  label: result.username,
  subLabel: result.name,
  query: 'users',
  relevancyScore: result.score,
  imgUrl: getAvatarOrPlaceholder(result.avatarUrl, result.id),
  isLocal: result?.isLocal,
});

export const formatESResult = (result: ESResult, isCollaborator = false): FilterResult => ({
  id: result.id,
  label: result.title,
  subLabel: formatESSubLabel(result),
  query: esModelToQuery(result.model, isCollaborator),
  relevancyScore: result.score,
  ...(result.imageUrl && { imgUrl: getAvatarOrPlaceholder(result.imageUrl, result.id) }),
});

const formatImportResult = (result: TrackImportResultData): FilterResult => ({
  id: result.publicId,
  imgUrl: '',
  label: result.name,
  subLabel: `Import (${result.count} posts)`,
  query: 'import',
  relevancyScore: 1,
});

const formatRoleResult = (result: RoleSearchResultData): FilterResult => ({
  id: result.id,
  label: result.name,
  subLabel: 'role',
  query: 'role',
  relevancyScore: result.score,
  isLocal: result?.isLocal,
});

const formatAccountResult = (result: AccountSearchResultData): FilterResult => ({
  id: result.id,
  label: result.account,
  subLabel: 'posting account',
  query: 'postingAccount',
  relevancyScore: result.score,
  isLocal: result?.isLocal,
});

const formatTagResult = (result: TagSearchResultData): FilterResult => ({
  id: result.id,
  label: result.value,
  subLabel: result.tagType,
  query: 'tags',
  relevancyScore: result.score,
  isLocal: result?.isLocal,
});

const formatEventResult = (result: TagSearchResultData): FilterResult => ({
  id: result.id,
  label: result.value,
  subLabel: result.tagType,
  query: 'events',
  relevancyScore: result.score,
  isLocal: result?.isLocal,
});

const formatCollaboratorTypeResult = (result: RoleSearchResultData): FilterResult => ({
  id: result.id,
  label: result.name,
  subLabel: 'category',
  query: 'byCollaboratorType',
  relevancyScore: result.score,
  isLocal: result?.isLocal,
});

const formatCollaboratorRoleResult = (result: RoleSearchResultData): FilterResult => ({
  id: result.id,
  label: result.name,
  subLabel: 'role',
  query: 'byCollaboratorRole',
  relevancyScore: result.score,
  isLocal: result?.isLocal,
});

const formatCollaboratorResult = (result: UserSearchResultData): FilterResult => ({
  id: result.id,
  label: result.username,
  subLabel: result.name,
  query: 'byCollaborator',
  relevancyScore: result.score,
  imgUrl: getAvatarOrPlaceholder(result.avatarUrl, result.id),
  isLocal: result?.isLocal,
});

/**
 * filterOptions is a custom matcher we use in multifilter autocompletes.
 * If an option is 'local' e.g. it is added to the multifilter via props, outside
 * of an API call with the input text, then we filter it.
 *
 * If an option is not local, the backend has already filtered it and we don't need a second
 * filter on the frontend.
 *
 * Some filter options have a meaningful subLabel e.g. the user label is the user's name and the
 * subLabel is the user's username. In these cases we match on both the label and subLabel.
 *
 * In most cases, though, we only match on the label as the subLabel is just additional context
 * for the user.
 *
 * */
export const filterOptions = (
  options: FilterResult[],
  filterState: FilterOptionsState<FilterResult>,
) => {
  // Remove # and @ from inputValue so we don't try to match on it.
  const cleanInput = filterState.inputValue.replace(/@|#/, '');
  if (!cleanInput) {
    return options;
  }
  return filter(options, (opt) => {
    if (!opt?.isLocal) {
      // If the option is not local (from the server) we assume it's
      // already been filtered
      return true;
    }

    // If it is local, have different rules depending on the type
    // The two types that have meaningful subLabels are 'users' and 'byCollaborator'
    const matchText: string = ['users', 'byCollaborator'].includes(opt.query)
      ? `${opt.label} ${opt.subLabel}`
      : opt.label;

    if (matchText.match(RegExp(escapeRegExp(cleanInput), 'i'))) {
      return true;
    }
    return false;
  });
};

export const formatFilterResults = (
  searchResults: SearchResultResponse, showES?: boolean, isCollaborator = false,
): FilterResult[] => {
  if (showES && searchResults.esResults) {
    const esResults = searchResults.esResults.map((e) => formatESResult(e, isCollaborator)) || [];
    const importResults = searchResults.importResults?.map((e) => formatImportResult(e)) || [];
    return orderBy([...esResults, ...importResults], ['relevancyScore'], ['desc']);
  }
  const users = searchResults.users?.map((user) => formatUserResult(user)) || [];
  const tags = searchResults.tagValues?.filter((tag) => tag.tagTypeId !== TAG_TYPE_IDS.event)
    .map((tag) => formatTagResult(tag)) || [];
  const events = searchResults.tagValues?.filter((tag) => tag.tagTypeId === TAG_TYPE_IDS.event)
    .map((tag) => formatEventResult(tag)) || [];
  const roles = searchResults.roles?.map((role) => formatRoleResult(role)) || [];
  const accounts = searchResults.accounts?.map((account) => formatAccountResult(account)) || [];

  return orderBy([...users, ...tags, ...events, ...roles, ...accounts], ['relevancyScore'], ['desc']);
};

export const formatProfileFilterResults = (searchResults: SearchResultResponse): FilterResult[] => {
  const accounts = searchResults.accounts?.map((account) => formatAccountResult(account)) || [];
  const users = searchResults.users?.map((user) => formatUserResult(user)) || [];
  const tags = searchResults.tagValues?.filter((tag) => tag.tagTypeId !== TAG_TYPE_IDS.event)
    .map((tag) => formatTagResult(tag));
  const events = searchResults.tagValues?.filter((tag) => tag.tagTypeId === TAG_TYPE_IDS.event)
    .map((tag) => formatEventResult(tag));
  const roles = searchResults.roles?.map((role) => formatRoleResult(role)) || [];

  return [...accounts, ...users, ...roles, ...tags, ...events];
};

export const formatCreatorSearchFilterResults = (
  searchResults: SearchResultResponse,
): FilterResult[] => {
  const users = searchResults.users?.map((user) => formatUserResult(user)) || [];
  const roles = searchResults.roles?.map((role) => formatRoleResult(role)) || [];
  const tags = searchResults.tagValues?.filter((tag) => tag.tagTypeId !== TAG_TYPE_IDS.event)
    .map((tag) => formatTagResult(tag));
  const events = searchResults.tagValues?.filter((tag) => tag.tagTypeId === TAG_TYPE_IDS.event)
    .map((tag) => formatEventResult(tag));

  return orderBy([...users, ...roles, ...tags, ...events], ['relevancyScore'], ['desc']);
};

export const formatCollaboratorRoleSearchFilterResults = (
  searchResults: SearchResultResponse,
): FilterResult[] => {
  const roles = searchResults.roles?.map((role) => formatCollaboratorRoleResult(role)) || [];
  const roleTags = searchResults.roleTags?.map(
    (roleTag) => formatCollaboratorTypeResult(roleTag),
  ) || [];
  const users = searchResults.users?.map((role) => formatCollaboratorResult(role)) || [];

  return orderBy([...roleTags, ...roles, ...users], ['relevancyScore'], ['desc']);
};
